<script setup lang="ts">
    import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from "vue"
    import type { ComponentPublicInstance } from "vue"
    import { storeToRefs } from "pinia"

    import { Country, Status, CategorySet } from "~/js/interfaces/typesense"
    import { Contact } from "~/js/interfaces/locations"
    import { useLocationsState } from "~/js/stores/locations";
    import { getGlobalTranslation } from "~/js/composables/translations";

    import AlertNoResults from "~/vue/molecules/alerts/AlertNoResults.vue";
    import ButtonPrimary from "~/vue/atoms/buttons/ButtonPrimary.vue";
    import CardLocation from "~/vue/molecules/cards/CardLocation.vue";
    import CardLocationSkeleton from "~/vue/molecules/cards/CardLocationSkeleton.vue";
    import CardContact from "~/vue/molecules/cards/CardContact.vue"
    import FormLocationFilter from "~/vue/organisms/forms/FormLocationFilter.vue";
    import TextH2 from "~/vue/atoms/texts/TextH2.vue";
    import MapLocationPlaceholder from "~/vue/organisms/maps/MapLocationPlaceholder.vue";

    // BC-40: lazy-load the map (mapbox-gl is ~1.8 MB) so it loads after first
    // paint instead of blocking the warehouse overview. The placeholder reserves
    // the map's height — matching the BC-33 server-rendered placeholder — so the
    // chunk arriving causes no layout shift.
    const MapLocation = defineAsyncComponent({
        loader: () => import("~/vue/organisms/maps/MapLocation.vue"),
        loadingComponent: MapLocationPlaceholder,
        delay: 0,
    })

    // MON-35: BC-40 only split mapbox-gl into its own chunk — the map still
    // mounted (and executed ~5.6s of JS on throttled mobile) during the initial
    // overview load. Gate the mount behind an IntersectionObserver on the
    // placeholder: on mobile the map sits below the cards, so it is not loaded
    // during first paint, nor for visitors (or the Lighthouse bot) who never
    // scroll to it. The chunk still loads on demand via defineAsyncComponent the
    // first time MapLocation renders. Once triggered it stays mounted.
    const loadMap = ref(false)
    const mapGate = ref<ComponentPublicInstance | null>(null)
    let mapObserver: IntersectionObserver | null = null

    // MON-42: below the `md` breakpoint the overview is a single column and the
    // map sits below the cards — most mobile sessions browse the list and never
    // need it. Make the map opt-in there (tap "Show map") instead of
    // auto-loading on scroll, so mapbox-gl never evaluates for those visitors
    // (nor the Lighthouse mobile bot). Tablet/desktop (>= md, where the 2-col
    // layout puts the map in the viewport) keep the IntersectionObserver.
    const mobileQuery = window.matchMedia("(max-width: 767px)")
    const isMobile = ref(mobileQuery.matches)
    const onMobileChange = (event: MediaQueryListEvent) => {
        isMobile.value = event.matches
    }

    const triggerMapLoad = () => {
        loadMap.value = true
        mapObserver?.disconnect()
        mapObserver = null
    }

    watch(mapGate, (instance) => {
        if (!instance || loadMap.value || isMobile.value) return

        const el = instance.$el as HTMLElement | null
        if (!el) return

        // No IntersectionObserver (very old browsers): load the map immediately.
        if (!("IntersectionObserver" in window)) {
            triggerMapLoad()
            return
        }

        mapObserver?.disconnect()
        // rootMargin preloads the map ~300px before it scrolls into view for a
        // seamless reveal, while staying well clear of the mobile fold.
        mapObserver = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    triggerMapLoad()
                }
            },
            { rootMargin: "300px 0px" },
        )

        mapObserver.observe(el)
    })

    onBeforeUnmount(() => {
        mapObserver?.disconnect()
        mapObserver = null
        mobileQuery.removeEventListener("change", onMobileChange)
    })

    /**
     * INTERFACES
     */
    interface FilterSets {
        statusses: Array<Status | null>;
        countries: Array<Country | null>;
    }

    type Contacts = Array<Contact | null>;

    export interface Props {
        filterSets: FilterSets | null;
        contacts: Contacts | null;
    }

    /**
     * VARIABLES
     * **/
    // props
    const props = defineProps<Props>();
    const ids = {
        'belgium': 1,
        'france': 2,
        'germany': 3,
        'netherlands': 4,
    };

    /**
     * STORE
     */
    const locationsStore = useLocationsState()

    const {
        locations,
        filters, 
        loading,
        pagination 
    } = storeToRefs(locationsStore)

    /**
     * VARIABLES
     */
    const facetSet: Array<CategorySet> = [
        {
            type: "countries",
            countries: props?.filterSets?.countries,
            isFacet: false,
        },
        {
            type: "statusses",
            statusses: props?.filterSets?.statusses,
            isFacet: false,
        },
    ];

    const urlCountries = ref(new URLSearchParams(window.location.search).getAll('countries'))

    /**
     * COMPUTED
     */
    const filteredContacts = computed(() => {
        const countriesParam = urlCountries.value;
        
        if (!countriesParam) return (props.contacts?.filter(contact => contact !== null) || []) as Contact[];
        
        // Flip the ids object to get id -> country mapping
        const countryMap = Object.fromEntries(
            Object.entries(ids).map(([country, id]) => [id, country])
        );
        
        // Get selected countries from URL
        const selectedCountries = countriesParam
            .flatMap(param => param.split(',')) // Handle both formats: multiple params or comma-separated
            .map(id => countryMap[id])
            .filter(Boolean);
        
        // Filter contacts - note: props.contacts.contacts (not props.contacts.value)
        return (props.contacts?.filter(contact => 
            contact !== null && selectedCountries.includes(contact.country)
        ) || []) as Contact[];
    });

    const noResultsMessage = computed(() => {
        let prose = `
            <p>${getGlobalTranslation('No locations were found in this region.')}</p>
            <p>${getGlobalTranslation('Interested in renting a warehouse ? Get in touch with us!')}</p>
        `

        return prose;
    })

    /**
     * METHODS
     **/
    const onSearch = (scrollTo: false) => {
        if (scrollTo) {
            const boundingClientRect = document
                .getElementById("teleport-locations")
                ?.getBoundingClientRect();

            if (boundingClientRect) {
                window.scrollTo({
                    top: boundingClientRect.top + window.scrollY - 110, // Adjusting scroll position
                    behavior: "smooth",
                });
            }
        }

        locationsStore.fetch(true);

         // Update the URL countries ref after search
        urlCountries.value = new URLSearchParams(window.location.search).getAll('countries')
    };

    /**
     * WATCHERS
     **/
    watch(() => filters.value, () => {
        urlCountries.value = new URLSearchParams(window.location.search).getAll('countries')
    }, { deep: true })

    const onLoadMore = () => {
        locationsStore.fetch(false);
    }

    /**
     * REHYDRATION (BC-31 / BC-32 / BC-33)
     */

    // Store defaults — keep in lockstep with useLocationsState() state and the
    // PHP warehouseSeed() status filter. The seed only matches the live view
    // when no filter has diverged from these.
    const DEFAULT_COUNTRIES = [1, 2, 3, 4];
    const DEFAULT_STATUSSES = [1, 3];

    const sameSet = (a: number[], b: number[]): boolean => {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((value, index) => value === sortedB[index]);
    };

    // The page server-renders a placeholder (default cards + map) inside
    // #teleport-locations — the very element this component teleports into.
    // Clear it synchronously, here in setup, BEFORE our Teleport renders, so Vue
    // replaces it in place: no duplicated markup, no flash, no layout shift.
    document.getElementById("teleport-locations")?.replaceChildren();

    // Always paint the SSR seed on the first frame — even on deep-linked /
    // filtered URLs (MON-38). The seed holds the default-view warehouses; it
    // flips loading=false and populates `locations`, so the first frame shows
    // real cards instead of skeletons in every case.
    const seeded = locationsStore.seedFromSsr();

    // The seed is the final answer only when the requested view matches the
    // default (no deep-link filters, no persisted custom filters). Otherwise we
    // still need Typesense to reconcile the actual filter set.
    const matchesDefaultView =
        !window.location.search &&
        sameSet(filters.value.countries.values, DEFAULT_COUNTRIES) &&
        sameSet(filters.value.statusses.values, DEFAULT_STATUSSES);

    /**
     * LIFECYCLES
     */

    onMounted(() => {
        mobileQuery.addEventListener("change", onMobileChange)

        if (!seeded || !matchesDefaultView) {
            // Reconcile against the real query-string / persisted filters. When
            // the seed is already painted, fetch WITHOUT flipping to skeletons
            // (stale-while-revalidate): the seeded cards stay until the fresh
            // results swap in. Only show skeletons when there was no seed to
            // paint in the first place.
            locationsStore.fetch(true, true, !seeded);
        }
    })
</script>

<template>
    <Teleport to="#teleport-locations">
        <fieldset>
            <legend class="sr-only">
                {{  getGlobalTranslation("Set global filtering for locations") }}
            </legend>

            <div class="flex flex-col md:flex-row md:items-center gap-6 justify-between mb-4">
                <h2>
                    <TextH2>
                        {{ getGlobalTranslation("Where are you interested in doing business?") }}
                    </TextH2>
                </h2>
            </div>

            <FormLocationFilter
                :countries="filterSets?.countries ? Object.values(filterSets.countries) : null"
                :default-countries="filters.countries.values"
                :default-statusses="filters.statusses.values"
                :statusses="filterSets?.statusses ? Object.values(filterSets.statusses) : null"
                @search="onSearch($event)"
                utilities="mb-8"
            />
        </fieldset>

        <!-- location cards -->
        <section class="grid md:grid-cols-2 gap-4 lg:gap-6">
            <div class="grid md:grid-cols-2 gap-4 lg:gap-6 items-start auto-rows-min">
                <template v-if="loading">
                    <CardLocationSkeleton />
                    <CardLocationSkeleton />
                    <CardLocationSkeleton />
                    <CardLocationSkeleton />
                    <CardLocationSkeleton />
                    <CardLocationSkeleton />
                </template>

                <template v-if="locations && !loading">
                    <CardLocation
                        v-for="location in locations.map((location) => location.document)"
                        :key="location.id"
                        :card="location"
                    />

                    <!-- load more -->
                    <div
                        v-if="
                            !loading &&
                            pagination.current < pagination.totalPages &&
                            pagination.current != pagination.totalPages
                        "
                        class="md:col-span-2"
                    >
                        <ButtonPrimary
                            utilities="mx-auto !block mt-4"
                            type="button"
                            :content="getGlobalTranslation('Load more')"
                            @click="onLoadMore"
                        />
                    </div>
                </template>

                <template v-if="locations && locations?.length === 0 && !loading">
                    <!-- no results -->
                    <AlertNoResults
                        :message="noResultsMessage"
                    />

                    <CardContact 
                        v-for="contact in filteredContacts"
                        :key="contact.id"
                        :card="contact"
                    />
                </template>
            </div>

            <template v-if="locations && !loading">
                <!-- MON-35: desktop/tablet load mapbox-gl once the placeholder
                     nears the viewport (IntersectionObserver gate in setup).
                     MON-42: mobile defers it to an explicit "Show map" tap. -->
                <MapLocation
                    v-if="loadMap"
                    :locations="locations"
                />
                <MapLocationPlaceholder
                    v-else-if="isMobile"
                    interactive
                    :label="getGlobalTranslation('Show map')"
                    @load="triggerMapLoad"
                />
                <MapLocationPlaceholder
                    v-else
                    ref="mapGate"
                />
            </template>
        </section>
    </Teleport>
</template>